import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Trash2, MessageCircle } from "lucide-react";
import { adminApi, SERVICE_LABEL, DIRT_LABEL, waLink, formatErr } from "@/lib/site";
import { Logo } from "@/components/site/Logo";

const FILTERS = [["todos", "Todos"], ["comercial", "Comercial"], ["residencial", "Residencial"], ["pos_obra", "Pós-obra"], ["pendente", "Pendentes"], ["atendido", "Atendidos"]];
const COLORS = { comercial: "#0F4C3A", pos_obra: "#121518" };

const Details = ({ q }) => {
  const rows = [
    ["Empresa", q.company], ["Estabelecimento", q.establishment_type], ["Imóvel", q.property_type], ["Salas", q.rooms],
    ["Tamanho", q.size], ["Metragem", q.area_m2 ? `${q.area_m2} m² (ref. a partir de R$ ${(q.area_m2 * 12).toLocaleString("pt-BR")})` : null],
    ["Sujeira", DIRT_LABEL[q.dirt_level]], ["Frequência", q.frequency], ["Horário", q.time_preference], ["Contrato", q.contract_interest],
  ].filter(([, v]) => v);
  return (
    <div className="text-sm">
      <p className="font-medium">{SERVICE_LABEL[q.service] ?? q.service}{q.visit_request && <span className="ml-2 text-xs bg-[#C59B27] text-[#121518] px-2 py-0.5">Visita técnica</span>}</p>
      {rows.map(([k, v]) => <p key={k} className="text-[#525860]"><span className="text-[#121518]">{k}:</span> {v}</p>)}
      {q.message && <p className="mt-2 text-[#525860] italic">"{q.message}"</p>}
    </div>
  );
};

const QuoteRow = ({ q, onStatus, onDelete }) => (
  <div data-testid="admin-quote-item" className="bg-white p-5 md:p-6 grid md:grid-cols-12 gap-4 items-start border-l-4" style={{ borderColor: COLORS[q.service] ?? "#C59B27" }}>
    <div className="md:col-span-4">
      <p className="font-serif-d text-2xl">{q.name}</p>
      <p className="text-sm text-[#525860]">{q.phone} · {q.location}</p>
      <p className="text-xs text-[#525860] mt-1">{new Date(q.created_at).toLocaleString("pt-BR")}</p>
    </div>
    <div className="md:col-span-4"><Details q={q} /></div>
    <div className="md:col-span-4 flex flex-wrap gap-2 md:justify-end">
      <button data-testid={`admin-status-${q.id}`} onClick={() => onStatus(q)}
        className={`rounded-full px-4 py-2 text-xs font-medium ${q.status === "atendido" ? "bg-[#0F4C3A] text-white" : "bg-[#F3F0EA] text-[#121518]"}`}>
        {q.status === "atendido" ? "Atendido" : "Pendente"}
      </button>
      <a href={waLink(`Olá ${q.name}! Aqui é da Limpeza Rápida SP sobre seu orçamento.`).replace("5511994576696", `55${q.phone.replace(/\D/g, "").replace(/^55/, "")}`)}
        target="_blank" rel="noreferrer" data-testid={`admin-wa-${q.id}`} className="rounded-full p-2 bg-[#25D366] text-white" aria-label="WhatsApp do cliente">
        <MessageCircle size={16} />
      </a>
      <button data-testid={`admin-delete-${q.id}`} onClick={() => onDelete(q)} className="rounded-full p-2 bg-[#F3F0EA] hover:bg-red-100" aria-label="Excluir">
        <Trash2 size={16} />
      </button>
    </div>
  </div>
);

export default function AdminDashboard() {
  const nav = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [filter, setFilter] = useState("todos");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await adminApi().get("/quotes");
      setQuotes(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err.response?.status === 401) { localStorage.removeItem("lrsp_token"); nav("/admin"); }
      else toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [nav]);

  useEffect(() => { load(); }, [load]);

  const onStatus = async (q) => {
    const status = q.status === "atendido" ? "pendente" : "atendido";
    const { data } = await adminApi().patch(`/quotes/${q.id}`, { status });
    setQuotes((qs) => qs.map((x) => (x.id === q.id ? data : x)));
  };
  const onDelete = async (q) => {
    if (!window.confirm(`Excluir pedido de ${q.name}?`)) return;
    await adminApi().delete(`/quotes/${q.id}`);
    setQuotes((qs) => qs.filter((x) => x.id !== q.id));
    toast.success("Pedido excluído");
  };
  const logout = () => { localStorage.removeItem("lrsp_token"); nav("/admin"); };

  const shown = quotes.filter((q) => filter === "todos" || q.service === filter || q.status === filter);
  const count = (k) => quotes.filter((q) => q.service === k || q.status === k).length;

  return (
    <div className="min-h-screen bg-[#FAF8F5]" data-testid="admin-dashboard">
      <header className="bg-[#121518] px-5 md:px-10 py-5 flex items-center justify-between">
        <Logo light />
        <button data-testid="admin-logout" onClick={logout} className="text-[#FAF8F5]/80 hover:text-white flex items-center gap-2 text-sm"><LogOut size={16} /> Sair</button>
      </header>
      <main className="max-w-[1200px] mx-auto px-5 md:px-10 py-10">
        <h1 className="font-serif-d text-5xl">Pedidos de orçamento</h1>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8">
          {[["Total", quotes.length], ["Comercial", count("comercial")], ["Residencial", count("residencial") + count("diarista")], ["Pós-obra", count("pos_obra")]].map(([l, v]) => (
            <div key={l} className="bg-white p-4 md:p-6" data-testid={`admin-stat-${l.toLowerCase().normalize("NFD").replace(/[^a-z]/g, "")}`}>
              <p className="font-mono-d text-[11px] tracking-[0.2em] text-[#0F4C3A] uppercase">{l}</p>
              <p className="font-serif-d text-4xl mt-1">{v}</p>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 mt-8" data-testid="admin-filter-status">
          {FILTERS.map(([k, l]) => (
            <button key={k} data-testid={`admin-filter-${k}`} onClick={() => setFilter(k)}
              className={`rounded-full px-4 py-2 text-sm transition-colors ${filter === k ? "bg-[#0F4C3A] text-white" : "bg-white text-[#525860] hover:text-[#121518]"}`}>{l}</button>
          ))}
        </div>
        <div className="mt-6 space-y-3" data-testid="admin-quote-table">
          {loading && <p className="text-[#525860]">Carregando…</p>}
          {!loading && shown.length === 0 && <p className="text-[#525860]" data-testid="admin-empty">Nenhum pedido encontrado.</p>}
          {shown.map((q) => <QuoteRow key={q.id} q={q} onStatus={onStatus} onDelete={onDelete} />)}
        </div>
      </main>
    </div>
  );
}
